import React, { useState } from 'react';
import { ContentData } from '../data/contentData';

interface AcademicsPageProps {
  content: ContentData;
}

const AcademicsPage: React.FC<AcademicsPageProps> = ({ content }) => {
  const [activeTab, setActiveTab] = useState('curriculum');

  const academicLevels = [
    {
      level: 'প্রাথমিক শিক্ষা',
      classes: '১ম - ৫ম শ্রেণি',
      age: '৬-১০ বছর',
      duration: '৫ বছর',
      icon: '🎒',
      description: 'মৌলিক শিক্ষার ভিত্তি গড়ে তোলা',
      subjects: ['বাংলা', 'ইংরেজি', 'গণিত', 'বিজ্ঞান', 'সামাজিক বিজ্ঞান', 'ধর্ম শিক্ষা', 'শারীরিক শিক্ষা'],
      features: [
        'খেলার মাধ্যমে শেখা',
        'সৃজনশীল কার্যক্রম',
        'নৈতিক শিক্ষা',
        'ভাষা দক্ষতা উন্নয়ন'
      ]
    },
    {
      level: 'মাধ্যমিক শিক্ষা',
      classes: '৬ষ্ঠ - ১০ম শ্রেণি',
      age: '১১-১৫ বছর',
      duration: '৫ বছর',
      icon: '📚',
      description: 'শিক্ষার গভীরতা বৃদ্ধি ও দক্ষতা বিকাশ',
      subjects: ['বাংলা', 'ইংরেজি', 'গণিত', 'পদার্থবিজ্ঞান', 'রসায়ন', 'জীববিজ্ঞান', 'ইতিহাস', 'ভূগোল', 'ধর্ম শিক্ষা'],
      features: [
        'বৈজ্ঞানিক চিন্তাধারা',
        'গবেষণা দক্ষতা',
        'দলগত কাজ',
        'প্রজেক্ট ভিত্তিক শেখা'
      ]
    },
    {
      level: 'উচ্চ মাধ্যমিক',
      classes: '১১শ - ১২শ শ্রেণি',
      age: '১৬-১৭ বছর',
      duration: '২ বছর',
      icon: '🎓',
      description: 'বিশ্ববিদ্যালয়ের জন্য প্রস্তুতি ও বিশেষায়িত শিক্ষা',
      subjects: ['বাংলা', 'ইংরেজি', 'গণিত', 'পদার্থবিজ্ঞান', 'রসায়ন', 'জীববিজ্ঞান', 'ইতিহাস', 'ভূগোল', 'অর্থনীতি', 'সমাজবিজ্ঞান'],
      features: [
        'বিশ্ববিদ্যালয় প্রস্তুতি',
        'বিশেষায়িত বিষয়',
        'গবেষণা প্রকল্প',
        'ক্যারিয়ার গাইডেন্স'
      ]
    }
  ];

  const curriculumAreas = [
    {
      name: 'বিজ্ঞান বিভাগ',
      icon: '🔬',
      subjects: ['পদার্থবিজ্ঞান', 'রসায়ন', 'জীববিজ্ঞান', 'গণিত', 'কম্পিউটার বিজ্ঞান'],
      description: 'বৈজ্ঞানিক চিন্তাধারা ও গবেষণা দক্ষতা বিকাশ'
    },
    {
      name: 'মানবিক বিভাগ',
      icon: '📖',
      subjects: ['বাংলা', 'ইংরেজি', 'ইতিহাস', 'ভূগোল', 'সমাজবিজ্ঞান', 'অর্থনীতি'],
      description: 'ভাষা দক্ষতা ও সামাজিক সচেতনতা বৃদ্ধি'
    },
    {
      name: 'বাণিজ্য বিভাগ',
      icon: '💼',
      subjects: ['অর্থনীতি', 'ব্যবসায় শিক্ষা', 'হিসাববিজ্ঞান', 'ব্যাংকিং'],
      description: 'ব্যবসায়িক দক্ষতা ও আর্থিক সাক্ষরতা'
    },
    {
      name: 'ধর্মীয় শিক্ষা',
      icon: '🕌',
      subjects: ['ইসলাম শিক্ষা', 'নৈতিক শিক্ষা', 'আধ্যাত্মিক বিকাশ'],
      description: 'নৈতিক মূল্যবোধ ও আধ্যাত্মিক বিকাশ'
    }
  ];

  const tabs = [
    { id: 'curriculum', label: 'পাঠ্যক্রম', icon: '📚' },
    { id: 'levels', label: 'শিক্ষা স্তর', icon: '🎓' },
    { id: 'programs', label: 'শিক্ষা কার্যক্রম', icon: '⏰' },
    { id: 'assessment', label: 'মূল্যায়ন', icon: '📊' }
  ];

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 bangla-font">{content.academics.title}</h1>
          <p className="text-lg text-gray-600 bangla-font">মানসম্পন্ন শিক্ষা ও আধুনিক পাঠ্যক্রম</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                <span className="bangla-font">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Curriculum Areas */}
        {activeTab === 'curriculum' && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center bangla-font">{content.academics.curriculum}</h2>
              <p className="text-center text-gray-600 mb-8 bangla-font">{content.academics.curriculumText}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {curriculumAreas.map((area, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6">
                    <div className="text-4xl mb-4 text-center">{area.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center bangla-font">{area.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 text-center bangla-font">{area.description}</p>
                    <div className="space-y-2">
                      {area.subjects.map((subject, idx) => (
                        <div key={idx} className="text-xs bg-white px-3 py-1 rounded-full text-center bangla-font">
                          {subject}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Academic Levels */}
        {activeTab === 'levels' && (
          <div className="space-y-8">
            {academicLevels.map((level, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-start space-x-6">
                  <div className="text-6xl">{level.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 bangla-font">{level.level}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <span className="font-semibold text-gray-700 bangla-font">শ্রেণি: </span>
                        <span className="text-gray-600 bangla-font">{level.classes}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700 bangla-font">বয়স: </span>
                        <span className="text-gray-600 bangla-font">{level.age}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700 bangla-font">সময়কাল: </span>
                        <span className="text-gray-600 bangla-font">{level.duration}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 bangla-font">{level.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 bangla-font">বিষয়সমূহ:</h4>
                        <div className="flex flex-wrap gap-2">
                          {level.subjects.map((subject, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm bangla-font">
                              {subject}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 bangla-font">বিশেষ বৈশিষ্ট্য:</h4>
                        <ul className="space-y-1">
                          {level.features.map((feature, idx) => (
                            <li key={idx} className="text-sm text-gray-600 bangla-font">• {feature}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Other tabs placeholder */}
        {activeTab === 'programs' && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center bangla-font">শিক্ষা কার্যক্রম</h2>
            <p className="text-center text-gray-600 bangla-font">শিক্ষা কার্যক্রমের বিস্তারিত তথ্য যোগ করা হচ্ছে...</p>
          </div>
        )}

        {activeTab === 'assessment' && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center bangla-font">মূল্যায়ন পদ্ধতি</h2>
            <p className="text-center text-gray-600 bangla-font">মূল্যায়ন পদ্ধতির বিস্তারিত তথ্য যোগ করা হচ্ছে...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademicsPage;
